import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSIntroduction = () => {
    const consoleCode = `// Open browser console (F12 or Cmd+Shift+J)
// Type this and press Enter:
console.log('Hello, JavaScript!');

// Output: Hello, JavaScript!`;

    const htmlInlineCode = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>My First JavaScript</title>
</head>
<body>
    <h1>JavaScript Demo</h1>
    
    <!-- Inline JavaScript --&gt;
    <script>
        console.log('Hello from inline script!');
        alert('Welcome to JavaScript!');
    </script>
</body>
</html>`;

    const htmlExternalCode = `<!-- index.html --&gt;
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>External JavaScript</title>
</head>
<body>
    <h1>JavaScript Demo</h1>
    
    <!-- Link external JavaScript file --&gt;
    <script src="script.js"></script>
</body>
</html>`;

    const scriptJsCode = `// script.js
console.log('Hello from external file!');

// Display current date
const today = new Date();
console.log('Today is:', today.toDateString());

// Simple calculation
const sum = 10 + 20;
console.log('10 + 20 =', sum);`;

    const practiceCode = `// Try these in your browser console:

// 1. Display a message
console.log('Hello, World!');

// 2. Perform calculations
console.log(10 + 5);
console.log(20 * 3);

// 3. Work with text
console.log('JavaScript' + ' is ' + 'awesome!');

// 4. Get current time
console.log(new Date());

// 5. Create a variable
let name = 'Your Name';
console.log('Hello, ' + name);`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">JavaScript Fundamentals</div>
                <h1>JavaScript Introduction & History</h1>
                <p className="hero-lead">
                    Discover the origins, evolution, and power of JavaScript - the language that powers
                    the modern web. From humble beginnings to becoming the world's most popular programming language.
                </p>
            </div>

            <div className="content-section">
                <h2>What is JavaScript?</h2>
                <p>
                    JavaScript is a high-level, interpreted programming language that enables interactive
                    web pages and is an essential part of web applications. It's the only programming language
                    that runs natively in web browsers, making it the "language of the web."
                </p>
                <p>
                    Unlike languages that require a complex compilation step, JavaScript is typically interpreted
                    by the browser's engine (like V8 in Chrome or SpiderMonkey in Firefox). This allows for rapid
                    development and an extremely fast feedback loop, which has contributed to its massive adoption
                    across the software engineering landscape.
                </p>

                <KeyFacts>
                    <ul>
                        <li> <strong>Ubiquitous Runtime:</strong> Beyond browsers, it powers backend services via Node.js, mobile apps via React Native, and even desktop apps via Electron.</li>
                        <li> <strong>Market Dominance:</strong> Over 98% of all websites use JavaScript on the client side, often in conjunction with frameworks like React, Vue, or Angular.</li>
                        <li> <strong>Concurrency Model:</strong> JavaScript uses a single-threaded event loop architecture, which is highly efficient for I/O-bound tasks common in web development.</li>
                        <li> <strong>Evolutionary Speed:</strong> The TC39 committee updates the ECMAScript standard annually, ensuring the language stays modern and competitive.</li>
                    </ul>
                </KeyFacts>
            </div>

            <div className="content-section">
                <h2>Brief History of JavaScript</h2>
                <p>
                    The story of JavaScript is one of rapid development and unexpected success. In 1995, Netscape
                    needed a scripting language for its browser, Netscape Navigator, to compete with Microsoft's
                    Internet Explorer. They tasked Brendan Eich with creating a language that looked like Java
                    but was easier to use.
                </p>

                <div className="timeline">
                    <div className="timeline-item">
                        <h3>1995 - Birth of JavaScript</h3>
                        <p>Created by <strong>Brendan Eich</strong> at Netscape in just <strong>10 days</strong>. Originally named "Mocha", then "LiveScript", it was eventually renamed to "JavaScript" for marketing purposes to leverage the popularity of Java at the time.</p>
                    </div>

                    <div className="timeline-item">
                        <h3>1997 - ECMAScript Standard</h3>
                        <p>To avoid fragmentation and ensure interoperability between browsers, JavaScript was standardized as <strong>ECMAScript (ES)</strong> by ECMA International. This provided a formal specification for implementation.</p>
                    </div>

                    <div className="timeline-item">
                        <h3>2005 - The AJAX Revolution</h3>
                        <p>Jesse James Garrett coined the term "AJAX" (Asynchronous JavaScript and XML). Google used this technique in Gmail and Google Maps to update parts of a page without a full reload, proving that JS could power complex desktop-like applications.</p>
                    </div>

                    <div className="timeline-item">
                        <h3>2009 - Node.js: JavaScript Everywhere</h3>
                        <p>Ryan Dahl released Node.js, which utilized Google's V8 engine to run JavaScript on the server. This transformed JavaScript from a client-side scripting language into a versatile, full-stack language.</p>
                    </div>

                    <div className="timeline-item">
                        <h3>2015 - ES6 (ES2015): The Modern Blueprint</h3>
                        <p>This was the largest update in the language's history. It introduced critical features like Arrow Functions, Classes, Modules, and Promises, making JavaScript suitable for enterprise-scale architecture.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Your First JavaScript Code</h2>
                <p>
                    One of JavaScript's greatest strengths is that you don't need any special installation to start
                    writing it. Every modern browser has a built-in "Engine" and "Console" ready to execute your code.
                </p>

                <h3>1. Using the Browser Console</h3>
                <p>
                    The console is an Interactive Development Environment (IDE) built right into your browser.
                    It follows the REPL (Read-Eval-Print Loop) pattern, allowing you to test snippets instantly.
                </p>
                <CodeBlock language="javascript" code={consoleCode} />

                <h3>2. Inlining JavaScript in HTML</h3>
                <p>
                    While not recommended for large-scale production apps, inlining allows you to bind
                    logic directly to your document structure using the <code>&lt;script&gt;</code> tag.
                </p>
                <CodeBlock language="html" code={htmlInlineCode} />

                <h3>3. Professional Approach: External Files</h3>
                <p>
                    In a production environment, code should be modular and separated from the UI structure.
                    This improves maintainability, caching, and allows for team collaboration.
                </p>
                <CodeBlock language="html" code={htmlExternalCode} />
                <CodeBlock language="javascript" code={scriptJsCode} />
            </div>

            <div className="content-section">
                <h2>JavaScript vs Java: The Great Confusion</h2>
                <p>
                    A common misconception among beginners is that JavaScript is a version of Java. In reality,
                    they are as different as "Car" and "Carpet." While both use C-style syntax, their
                    underlying philosophies and architectures are vastly different.
                </p>

                <div className="responsive-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Feature</th>
                                <th>JavaScript</th>
                                <th>Java</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Execution</strong></td>
                                <td>Interpreted (JIT Compiled)</td>
                                <td>Compiled to Bytecode</td>
                            </tr>
                            <tr>
                                <td><strong>Typing</strong></td>
                                <td>Dynamic & Loose</td>
                                <td>Static & Strong</td>
                            </tr>
                            <tr>
                                <td><strong>Environment</strong></td>
                                <td>Browsers & Node.js</td>
                                <td>JVM (Java Virtual Machine)</td>
                            </tr>
                            <tr>
                                <td><strong>Object Model</strong></td>
                                <td>Prototypes</td>
                                <td>Classes (Traditional)</td>
                            </tr>
                            <tr>
                                <td><strong>Primary Use</strong></td>
                                <td>Web, Full-stack</td>
                                <td>Enterprise, Android, Backends</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>What Can You Build with JavaScript?</h2>
                <p>
                    JavaScript's versatility is unmatched. By mastering it, you gain the ability to build
                    software for almost any platform in existence today.
                </p>

                <div className="capabilities-grid">
                    <div className="capability-card">
                        <h3> Web Applications</h3>
                        <p>From simple landing pages to complex SPAs (Single Page Applications) like Facebook and Gmail. Modern JS frameworks handle the heavy lifting of state management and DOM rendering.</p>
                    </div>

                    <div className="capability-card">
                        <h3> Backend Systems</h3>
                        <p>Using Node.js, JS developers build highly scalable microservices and RESTful APIs, handling millions of concurrent connections efficiently.</p>
                    </div>

                    <div className="capability-card">
                        <h3> Mobile Apps</h3>
                        <p>React Native allows engineers to build native mobile apps for iOS and Android using a single JavaScript codebase, significantly reducing time-to-market.</p>
                    </div>

                    <div className="capability-card">
                        <h3> AI & Robotics</h3>
                        <p>With libraries like TensorFlow.js, you can run machine learning models directly in the browser, enabling privacy-focused and low-latency AI features.</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>The Modern Ecosystem</h2>
                <p>
                    Nobody writes "Vanilla" JavaScript in isolation for large projects. The ecosystem is
                    powered by a massive library of tools managed through <strong>NPM (Node Package Manager)</strong>.
                </p>
                <div className="ecosystem-grid">
                    <div className="ecosystem-item">
                        <h4>Popular Frameworks</h4>
                        <ul>
                            <li><strong>React:</strong> The industry standard for UI components.</li>
                            <li><strong>Next.js:</strong> The go-to for server-side rendered (SSR) apps.</li>
                            <li><strong>TypeScript:</strong> A superset of JS that adds static typing.</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <ProTip>
                    <p>
                        To become a professional JavaScript developer, you need the right tools. While any text
                        editor works, <strong>Visual Studio Code (VS Code)</strong> is the industry preference
                        due to its incredible IntelliSense and debugging capabilities.
                    </p>
                    <p className="mb-0"><strong>Recommendation:</strong> Always enable "Strict Mode" in your files to catch
                        common silent errors and prepare your code for future optimizations.</p>
                </ProTip>
            </div>

            <div className="content-section">
                <h2>Quick Hands-on Practice</h2>
                <p>
                    The best way to learn is by doing. Open your browser's console right now and try
                    interacting with these examples to see the immediate results.
                </p>
                <CodeBlock language="javascript" code={practiceCode} />
            </div>

        </div>
    );
};

export default JSIntroduction;




